import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './components/onboardingView/store/store';
import LoginPage from './LoginPage'; // Import the LoginPage component
import DemoForm from './DemoForm';
import './App.css';
import PracticesForPort from './practicesForPort';
import QueryTool from './QueryTool';
import VoIPConfiguration from './VoIPConfiguration';
import AddExtensions from './AddExtensions';
import OutboundAnalytics from './OutboundAnalytics';
import CallInsights from './CallInsights';
import { OnboardingViewWrapper } from './OnboardingViewWrapper';
import DemoCalls from './DemoCalls';
import SalesTouchpoints from './SalesTouchpoints';
import CreateOrgAndAdmin from './CreateOrgAndAdmin';
import QuoteBuilder from './QuoteBuilder';
import OrgRegistration from './OrgRegistration';
import WinReport from './WinReport';
import NewDashboard from './NewDashboard';
import LocationOnboarding from './LocationOnboarding';
import PayoutsTab from './PayoutsTab';
import { validateSession, fetchFromDentalAPI as sharedFetchFromDentalAPI, secureFetch } from './common';

// Save the intended URL before redirecting to login
function RedirectToLogin() {
  const currentPath = window.location.pathname;
  if (currentPath !== '/' && currentPath !== '/login') {
    sessionStorage.setItem('returnTo', currentPath);
  }
  return <Navigate to="/login" />;
}

const BASE_URL = 'https://newtonflaskserver-hfgkcrewf4fgedc6.westus-01.azurewebsites.net';
const DENTAL_API_URL = 'https://dentalexpressserver.azurewebsites.net';
//const DENTAL_API_URL = 'http://localhost:3001';

const fetchFromDentalAPI = sharedFetchFromDentalAPI;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      const isValid = await validateSession();
      if (isValid) {
        setUser({ authenticated: true });
      }
      setLoading(false);
    };
    checkSession();
  }, []);

  if (loading) {
    return <div>Loading...</div>; // Or a spinner component
  }
  
  return (
    <Provider store={store}>
      <Router>
        <Routes>
          <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" />} />
          <Route path="/payouts" element={user ? <PayoutsTab /> : <RedirectToLogin />} />
          <Route path="/*" element={user ? <AuthenticatedApp /> : <RedirectToLogin />} />
        </Routes>
      </Router>
    </Provider>
  );
}

const STANDALONE_PATHS = ['/form', '/quoteBuilder', '/orgRegistration', '/winReport'];

function AuthenticatedApp() {
  const [useLegacy, setUseLegacy] = useState(
    localStorage.getItem('useLegacyDashboard') === 'true' ||
    STANDALONE_PATHS.includes(window.location.pathname)
  );

  const switchToLegacy = () => {
    localStorage.setItem('useLegacyDashboard', 'true');
    setUseLegacy(true);
  };

  const switchToNew = () => {
    localStorage.removeItem('useLegacyDashboard');
    setUseLegacy(false);
  };

  if (STANDALONE_PATHS.includes(window.location.pathname)) {
    return <MainApp />;
  }

  if (useLegacy) {
    return <MainApp onSwitchToNew={switchToNew} />;
  }

  return <NewDashboard onSwitchToLegacy={switchToLegacy} />;
}

const MainApp = ({ onSwitchToNew }) => {
  /* ---------- shared state ---------- */
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState(
     window.location.pathname === '/form' ? 'form' 
     : window.location.pathname === '/quoteBuilder' ? 'quoteBuilder'
     : window.location.pathname === '/orgRegistration' ? 'orgRegistration'
     : window.location.pathname === '/winReport' ? 'winReport'
     : 'onboarding'
  );

  /* ---------- onboarding / registration ---------- */
  const [linkEmail, setLinkEmail] = useState('');
  const [registrationEmail, setRegistrationEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [registrationLink, setRegistrationLink] = useState('');
  const [practiceIds, setPracticeIds] = useState(['']); // Array of practice IDs
  const [emailStatus, setEmailStatus] = useState('');
  const [isAdminUser, setIsAdminUser] = useState(true);
  const [nonVoipOnboarding, setNonVoipOnboarding] = useState(false);

  /* ---------- pre-generated org registration ---------- */
  const [orgRegEmail, setOrgRegEmail] = useState('');
  const [orgRegOrgId, setOrgRegOrgId] = useState('');
  const [orgRegStatus, setOrgRegStatus] = useState('');

  const sendOrgRegistration = async () => {
    if (!orgRegEmail || !orgRegOrgId) {
      setOrgRegStatus('Please enter both email and organization ID');
      return;
    }

    try {
      setOrgRegStatus('Sending registration email…');

      const result = await fetchFromDentalAPI('/api/send-org-registration', {
        method: 'POST',
        body: JSON.stringify({
          email: orgRegEmail,
          organizationId: orgRegOrgId,
        }),
      });

      setOrgRegStatus(result.message || `Registration email sent (${result.practiceCount} practices)`);
      setOrgRegEmail('');
      setOrgRegOrgId('');
    } catch (error) {
      setOrgRegStatus(`Error: ${error.message}`);
    }
  };

  const [recallStats, setRecallStats] = useState(null);
  const [recallPeriod, setRecallPeriod] = useState('today');
  const [recallLoading, setRecallLoading] = useState(false);
  const [recallError, setRecallError] = useState('');
  const [practiceFilter, setPracticeFilter] = useState('');
  const [outboundStats, setOutboundStats] = useState({});



  /* ---------- NEW: Extension management state ---------- */
  const [searchPracticeId, setSearchPracticeId] = useState('');
  const [resolvedDomain, setResolvedDomain] = useState('');
  const [currentDisplayName, setCurrentDisplayName] = useState('');
  const [currentPracticeId, setCurrentPracticeId] = useState('');
  const [extensions, setExtensions] = useState([]);
  const [extensionsLoading, setExtensionsLoading] = useState(false);
  const [extensionsError, setExtensionsError] = useState('');
  const [editingExtension, setEditingExtension] = useState(null);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  const [domainLoading, setDomainLoading] = useState(false);

  /* ---------- NEW: BLF management state ---------- */
  const [blfModalOpen, setBlfModalOpen] = useState(false);
  const [currentBlfExtension, setCurrentBlfExtension] = useState(null);
  const [blfData, setBlfData] = useState(null);
  const [blfButtons, setBlfButtons] = useState([]);
  const [availableModes, setAvailableModes] = useState([]);
  const [blfLoading, setBlfLoading] = useState(false);
  const [blfError, setBlfError] = useState('');
  const [blfSaving, setBlfSaving] = useState(false);

  /* ---------- NEW: Ring Groups management state ---------- */
  const [ringGroups, setRingGroups] = useState([]);
  const [ringGroupsLoading, setRingGroupsLoading] = useState(false);
  const [ringGroupsError, setRingGroupsError] = useState('');
  const [phoneMaps, setPhoneMaps] = useState({});
  const [editingRingGroup, setEditingRingGroup] = useState(null);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [ringGroupSaving, setRingGroupSaving] = useState(false);

  /* ---------- demo-call scheduler ---------- */
  const [patientName, setPatientName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callStatus, setCallStatus] = useState('');
  
  /* ---------- NEW: PCAP analysis state ---------- */
  const [pcapAnalyses, setPcapAnalyses] = useState([]);
  const [pcapLoading, setPcapLoading] = useState(false);
  const [pcapError, setPcapError] = useState('');
  const [pcapPagination, setPcapPagination] = useState({
    page: 1,
    page_size: 20,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false
  });
  const [pcapFilters, setPcapFilters] = useState({
    call_id: '',
    has_problems: '',
    start_date: '',
    end_date: '',
    order_by: 'analysis_timestamp',
    order_direction: 'desc'
  });
  const [pcapSummary, setPcapSummary] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);

  /* ---------- NEW: Call Details state ---------- */
  const [searchCallId, setSearchCallId] = useState('');
  const [callDetails, setCallDetails] = useState(null);
  const [callDetailsLoading, setCallDetailsLoading] = useState(false);
  const [callDetailsError, setCallDetailsError] = useState('');
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingError, setRecordingError] = useState('');

  // Cleanup recording URL on unmount to prevent memory leak
  useEffect(() => {
    return () => {
      if (recordingUrl) {
        URL.revokeObjectURL(recordingUrl);
      }
    };
  }, [recordingUrl]);

  useEffect(() => {
    if (activeTab === 'form') {
      window.history.replaceState(null, '', '/form');
    } else if (activeTab === 'quoteBuilder') {
      window.history.replaceState(null, '', '/quoteBuilder');
    } else if (activeTab === 'orgRegistration') {
      window.history.replaceState(null, '', '/orgRegistration');
    } else if (activeTab === 'winReport') {
      window.history.replaceState(null, '', '/winReport' + window.location.search);
    } else if (window.location.pathname === '/form' || window.location.pathname === '/quoteBuilder' || window.location.pathname === '/orgRegistration' || window.location.pathname === '/winReport') {
      window.history.replaceState(null, '', '/');
    }
  }, [activeTab]);

  /* ---------- initial onboarding list fetch ---------- */
  useEffect(() => {
    fetchFromDentalAPI('/api/onboarding-users')
      .then((data) => {
        if (data.success) {
          setUsers(data.users);
        }
      })
      .catch(error => console.error("Failed to fetch onboarding users:", error));
  }, []);

  /* ---------- helper utilities ---------- */
  const getStageLabel = (stage) => {
    const stages = {
      '-1': 'Link Sent',
      '0': 'Registration Started',
      '1': 'Company Info Complete',
      '2': 'Practice Info Complete',
      '3': '10DLC Complete',
      '4': 'Phones Complete',
      '5': 'Doc Review Complete',
      '6': 'Onboarding Complete',
    };
    return stages[stage] || `Stage ${stage}`;
  };

  const formatPhoneNumber = (input) => {
    const digitsOnly = input.replace(/\D/g, '');
    if (digitsOnly.length === 10) return digitsOnly;
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1'))
      return digitsOnly.substring(1);
    return digitsOnly;
  };

  /* ---------- registration-related actions ---------- */
  const generateRegistrationLink = async () => {
    try {
      const result = await fetchFromDentalAPI('/api/get-registration-link', {
        method: 'POST',
        body: JSON.stringify({
          email: linkEmail,
          nonVoipOnboarding: nonVoipOnboarding,
        }),
      });
      
      if (result.success) {
        setRegistrationLink(result.registrationUrl);
      }
    } catch (error) {
      console.error('Error generating link:', error);
    }
  };

  const addPracticeIdField = () => {
    setPracticeIds([...practiceIds, '']);
  };

  const removePracticeIdField = (index) => {
    if (practiceIds.length > 1) {
      setPracticeIds(practiceIds.filter((_, i) => i !== index));
    }
  };

  const updatePracticeId = (index, value) => {
    const newPracticeIds = [...practiceIds];
    newPracticeIds[index] = value;
    setPracticeIds(newPracticeIds);
  };

  const sendRegistrationEmail = async () => {
    if (!registrationEmail) {
      setEmailStatus('Please enter an email');
      return;
    }

    const validPracticeIds = practiceIds.filter(id => id.trim());

    try {
      setEmailStatus('Sending registration email…');

      const result = await fetchFromDentalAPI('/api/send-registration', {
        method: 'POST',
        body: JSON.stringify({
          email: registrationEmail,
          firstName,
          lastName,
          organizationId,
          practiceId: validPracticeIds[0] || '', // Fallback for legacy
          practiceIds: validPracticeIds,
          bAdminUser: isAdminUser,
          nonVoipOnboarding 
        }),
      });

      setEmailStatus(result.message || 'Registration email sent successfully');
      setRegistrationEmail('');
      setFirstName('');
      setLastName('');
      setOrganizationId('');
      setPracticeIds(['']);
      setIsAdminUser(true);
    } catch (error) {
      setEmailStatus(`Error: ${error.message}`);
    }
  };

  /* ---------- NEW: demo-call scheduling ---------- */
  const scheduleDemo = async () => {
    const formattedNumber = formatPhoneNumber(phoneNumber);

    if (formattedNumber.length !== 10) {
      setCallStatus('Please enter a valid 10-digit phone number');
      return;
    }
    if (!patientName.trim()) {
      setCallStatus('Please enter the patient name');
      return;
    }


    const fullNumber = `+1${formattedNumber}`;

    try {
      setCallStatus('Scheduling demo call…');

      let url = "http://localhost:7071/api/DemoTriggerFunction"
      url = 'https://dentistdemo.azurewebsites.net/api/DemoTriggerFunction'
      url = 'https://newtonai2025.azurewebsites.net/api/DemoTriggerFunction'

      const response = await fetch(
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
            patient_name: patientName,
            phone_number: fullNumber,
            use_outbound_demo: true,
           }),
        }
      );

      const result = await response.json();
      if (response.ok && result.success) {
        setCallStatus(result.message || 'Demo call scheduled');
        setPatientName('');
        setPhoneNumber('');
      } else {
        setCallStatus(`Error: ${result.message || 'Failed to schedule demo'}`);
      }
    } catch (error) {
      setCallStatus(`Error: ${error.message}`);
    }
  };





  /* ---------- Helper function to map ring group names to phone numbers ---------- */
  const getPhoneNumbersForRingGroup = (ringGroupName) => {
    const phoneNumbers = [];
    
    // Look through all phone numbers in phoneMaps
    Object.entries(phoneMaps).forEach(([phoneNumber, sipTrunks]) => {
      // Check if any SIP trunk contains the ring group name
      const hasMatchingSipTrunk = sipTrunks.some(sipTrunk => 
        sipTrunk.includes(ringGroupName) || sipTrunk.includes(`${ringGroupName}@`)
      );
      
      if (hasMatchingSipTrunk) {
        phoneNumbers.push(phoneNumber);
      }
    });
    
    return phoneNumbers;
  };

  /* ---------- Ring Group Agent Management Functions ---------- */
  const startEditingRingGroup = (ringGroup) => {
    setEditingRingGroup(ringGroup);
    setSelectedAgents(ringGroup.agents || []);
  };

  const cancelEditingRingGroup = () => {
    setEditingRingGroup(null);
    setSelectedAgents([]);
  };

  const toggleAgentSelection = (agentExt) => {
    setSelectedAgents(prev => {
      if (prev.includes(agentExt)) {
        return prev.filter(ext => ext !== agentExt);
      } else {
        return [...prev, agentExt].sort((a, b) => parseInt(a) - parseInt(b));
      }
    });
  };

  const saveRingGroupAgents = async () => {
    if (!editingRingGroup) {
      alert('No ring group selected for editing');
      return;
    }

    setRingGroupSaving(true);
    
    try {
      const extensionList = selectedAgents.join(' ');
      
      const response = await secureFetch(
        `${BASE_URL}/api/ring-groups/${resolvedDomain}/${editingRingGroup.name}/update`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true'
          },
          body: JSON.stringify({
            extension_list: extensionList
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // Update the ring group in local state
        setRingGroups(prev => prev.map(rg => 
          rg.name === editingRingGroup.name 
            ? { ...rg, agents: selectedAgents }
            : rg
        ));
        
        // Clear editing state
        setEditingRingGroup(null);
        setSelectedAgents([]);
        
        alert(`Ring group ${editingRingGroup.name} updated successfully`);
      } else {
        throw new Error(result.message || 'Failed to update ring group');
      }
    } catch (error) {
      console.error('Error updating ring group:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setRingGroupSaving(false);
    }
  };

  /* ---------- NEW: Extension management functions ---------- */
  const lookupDomain = async () => {
    if (!searchPracticeId.trim()) {
      setExtensionsError('Please enter a practice ID');
      return;
    }

    setDomainLoading(true);
    setExtensionsError('');
    setResolvedDomain('');
    setCurrentDisplayName('');
    setCurrentPracticeId('');
    setExtensions([]);
    setRingGroups([]);
    setRingGroupsError('');
    setPhoneMaps({});
    setEditingRingGroup(null);
    setSelectedAgents([]);
    
    try {
      const response = await secureFetch(`${BASE_URL}/api/domain/${searchPracticeId}`, {
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success && data.domain_name) {
        // Use the complete domain name as returned from API
        setResolvedDomain(data.domain_name);
        setCurrentDisplayName(data.display_name);
        setCurrentPracticeId(searchPracticeId); // Use the original practice ID
        
        // Automatically fetch extensions after resolving domain
        await fetchExtensionsWithDomain(data.domain_name, searchPracticeId);
      } else {
        throw new Error(data.message || 'Domain not found');
      }
    } catch (error) {
      console.error('Error looking up domain:', error);
      setExtensionsError(`Failed to find domain: ${error.message}`);
    } finally {
      setDomainLoading(false);
    }
  };

  const fetchExtensionsWithDomain = async (domain, practiceId = null) => {
    setExtensionsLoading(true);
    setExtensionsError('');
    
    try {
      const response = await secureFetch(`${BASE_URL}/api/extensions/${domain}`, {
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setExtensions(data.extensions || []);
        // Also fetch ring groups when extensions are loaded - use passed practiceId or fallback to state
        fetchRingGroups(domain, practiceId || currentPracticeId);
      } else {
        throw new Error(data.message || 'Failed to fetch extensions');
      }
    } catch (error) {
      console.error('Error fetching extensions:', error);
      setExtensionsError(`Failed to load extensions: ${error.message}`);
      setExtensions([]);
    } finally {
      setExtensionsLoading(false);
    }
  };

  const fetchExtensions = async () => {
    if (!resolvedDomain) {
      setExtensionsError('Please search for a domain first');
      return;
    }
    
    await fetchExtensionsWithDomain(resolvedDomain, currentPracticeId);
    // Ring groups will be fetched automatically by fetchExtensionsWithDomain
  };

  const startRenameExtension = (extension) => {
    setEditingExtension(extension);
    setNewDisplayName(extension.display_name || '');
  };

  const cancelRename = () => {
    setEditingExtension(null);
    setNewDisplayName('');
  };

  const saveRename = async () => {
    if (!newDisplayName.trim()) {
      alert('Please enter a display name');
      return;
    }

    setRenameLoading(true);
    
    try {
      const response = await secureFetch(
        `${BASE_URL}/api/extensions/${resolvedDomain}/${editingExtension.name}/rename`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true'
          },
          body: JSON.stringify({
            display_name: newDisplayName
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // Update the extension in the local state
        setExtensions(prev => prev.map(ext => 
          ext.name === editingExtension.name 
            ? { ...ext, display_name: newDisplayName }
            : ext
        ));
        
        // Clear editing state
        setEditingExtension(null);
        setNewDisplayName('');
        
        alert(`Extension ${editingExtension.name} renamed successfully`);
      } else {
        throw new Error(result.message || 'Failed to rename extension');
      }
    } catch (error) {
      console.error('Error renaming extension:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setRenameLoading(false);
    }
  };

  /* ---------- recall stats functions ---------- */
const fetchRecallStats = async () => {
  setRecallLoading(true);
  setRecallError('');
  
  try {
    const data = await fetchFromDentalAPI(`/api/recall/analytics/all?period=${recallPeriod}`);
    setRecallStats(data);

    // Fetch outbound stats (recall-daily-stats) per practice from Flask server
    if (data?.practices?.length) {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const startDateStr = thirtyDaysAgo.toISOString();
      const endDateStr = now.toISOString();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';

      const token = localStorage.getItem('jwtToken');
      const results = await Promise.allSettled(
        data.practices.map(async (p) => {
          const endpoint = `${BASE_URL}/api/${p.practiceId}/recall-daily-stats?startDate=${startDateStr}&endDate=${endDateStr}&timezone=${timezone}`;
          const res = await fetch(endpoint, {
            headers: {
              'ngrok-skip-browser-warning': 'true',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
          });
          if (!res.ok) return { practiceId: p.practiceId, data: null };
          const json = await res.json();
          return { practiceId: p.practiceId, data: json };
        })
      );

      const statsMap = {};
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value?.data) {
          const { practiceId: pid, data: d } = r.value;
          statsMap[pid] = {
            outreach: d.outreach?.total ?? 0,
            bookings: (d.outreach?.bookingsMade ?? 0) + (d.outreach?.staffAssistedBookings ?? 0),
          };
        }
      }
      setOutboundStats(statsMap);
    }
  } catch (error) {
    console.error('Error fetching recall stats:', error);
    setRecallError(`Failed to load recall stats: ${error.message}`);
  } finally {
    setRecallLoading(false);
  }
};

const getFilteredPractices = () => {
  if (!recallStats || !recallStats.practices) return [];
  
  if (!practiceFilter) return recallStats.practices;
  
  return recallStats.practices.filter(practice => 
    practice.practiceId.toLowerCase().includes(practiceFilter.toLowerCase())
  );
};

useEffect(() => {
  if (activeTab === 'recallStats') {
    fetchRecallStats();
  }
  // eslint-disable-next-line
}, [activeTab, recallPeriod]);

  /* ---------- NEW: PCAP Analysis functions ---------- */
  const fetchPcapAnalyses = async (page = 1, filters = pcapFilters) => {
    setPcapLoading(true);
    setPcapError('');
    
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pcapPagination.page_size.toString(),
        order_by: filters.order_by,
        order_direction: filters.order_direction
      });
      
      // Add optional filters
      if (filters.call_id) params.append('call_id', filters.call_id);
      if (filters.has_problems) params.append('has_problems', filters.has_problems);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await fetch(`${BASE_URL}/api/pcap-analysis?${params.toString()}`, {
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setPcapAnalyses(data.data || []);
        setPcapPagination(data.pagination || {});
        console.log(`Loaded ${data.data?.length || 0} pcap analyses (page ${page})`);
      } else {
        throw new Error(data.message || 'Failed to fetch pcap analyses');
      }
    } catch (error) {
      console.error('Error fetching pcap analyses:', error);
      setPcapError(`Failed to load pcap analyses: ${error.message}`);
    } finally {
      setPcapLoading(false);
    }
  };

  const fetchPcapSummary = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/pcap-analysis/summary`, {
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setPcapSummary(data.summary);
      } else {
        throw new Error(data.message || 'Failed to fetch summary');
      }
    } catch (error) {
      console.error('Error fetching pcap summary:', error);
      // Don't set error state for summary as it's not critical
    }
  };

  const fetchAnalysisById = async (analysisId) => {
    try {
      const response = await fetch(`${BASE_URL}/api/pcap-analysis/${analysisId}`, {
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setSelectedAnalysis(data.data);
        setAnalysisModalOpen(true);
      } else {
        throw new Error(data.message || 'Failed to fetch analysis details');
      }
    } catch (error) {
      console.error('Error fetching analysis details:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handlePcapFilterChange = (field, value) => {
    setPcapFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyPcapFilters = () => {
    fetchPcapAnalyses(1, pcapFilters);
  };

  const clearPcapFilters = () => {
    const defaultFilters = {
      call_id: '',
      has_problems: '',
      start_date: '',
      end_date: '',
      order_by: 'analysis_timestamp',
      order_direction: 'desc'
    };
    setPcapFilters(defaultFilters);
    fetchPcapAnalyses(1, defaultFilters);
  };

  const goToPcapPage = (page) => {
    fetchPcapAnalyses(page, pcapFilters);
  };

  const closeAnalysisModal = () => {
    setAnalysisModalOpen(false);
    setSelectedAnalysis(null);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      // Handle Unix timestamps (seconds since epoch)
      if (typeof timestamp === 'string' && /^\d+\.\d+$/.test(timestamp)) {
        // Convert Unix timestamp to milliseconds
        const timestampMs = parseFloat(timestamp) * 1000;
        return new Date(timestampMs).toLocaleString();
      }
      // Handle Unix timestamps (integer seconds)
      if (typeof timestamp === 'number' || /^\d+$/.test(timestamp)) {
        const timestampMs = parseInt(timestamp) * 1000;
        return new Date(timestampMs).toLocaleString();
      }
      // Handle ISO strings and other formats
      return new Date(timestamp).toLocaleString();
    } catch (e) {
      return timestamp;
    }
  };

  const calculateCallDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    try {
      const start = parseFloat(startTime);
      const end = parseFloat(endTime);
      return Math.round(end - start);
    } catch (e) {
      return null;
    }
  };

  const formatPhoneDisplay = (sipUri) => {
    if (!sipUri) return 'N/A';
    try {
      // Extract number from SIP URI like "\"(860) 670-1301\" <sip:+18606701301@domain.com>"
      const displayMatch = sipUri.match(/^"([^"]+)"/);
      if (displayMatch) {
        return displayMatch[1];
      }
      
      // Extract from sip: format like "sip:1@domain.com"
      const sipMatch = sipUri.match(/sip:([^@]+)@/);
      if (sipMatch) {
        return sipMatch[1];
      }
      
      return sipUri;
    } catch (e) {
      return sipUri;
    }
  };

  // Fetch pcap data when tab becomes active
  useEffect(() => {
    if (activeTab === 'pcapAnalysis') {
      fetchPcapAnalyses();
      fetchPcapSummary();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  /* ---------- NEW: Call Details functions ---------- */
  const fetchCallDetails = async () => {
    if (!searchCallId.trim()) {
      setCallDetailsError('Please enter a call ID');
      return;
    }

    setCallDetailsLoading(true);
    setCallDetailsError('');
    setCallDetails(null);

    try {
      const response = await fetch(`${BASE_URL}/api/call-details/${encodeURIComponent(searchCallId)}`, {
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setCallDetails(data.data);
        console.log('Call details loaded:', data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch call details');
      }
    } catch (error) {
      console.error('Error fetching call details:', error);
      setCallDetailsError(`Failed to load call details: ${error.message}`);
    } finally {
      setCallDetailsLoading(false);
    }
  };

  const clearCallDetails = () => {
    setSearchCallId('');
    setCallDetails(null);
    setCallDetailsError('');
    // Clean up recording URL to avoid memory leaks
    if (recordingUrl) {
      URL.revokeObjectURL(recordingUrl);
    }
    setRecordingUrl(null);
    setRecordingError('');
  };

  const fetchRecording = async () => {
    if (!callDetails) {
      setRecordingError('No call details loaded');
      return;
    }

    const callId = callDetails.id;
    const practiceId = callDetails.practiceId;

    if (!callId || !practiceId) {
      setRecordingError('Call ID or Practice ID not available');
      return;
    }

    setRecordingLoading(true);
    setRecordingError('');
    
    // Clean up previous recording URL
    if (recordingUrl) {
      URL.revokeObjectURL(recordingUrl);
      setRecordingUrl(null);
    }

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${DENTAL_API_URL}/api/recording/${callId}/${practiceId}`, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        if (response.status === 404) {
          setRecordingError('No recording available for this call');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setRecordingUrl(url);
      console.log('Recording loaded successfully');
    } catch (error) {
      console.error('Error fetching recording:', error);
      setRecordingError(`Failed to load recording: ${error.message}`);
    } finally {
      setRecordingLoading(false);
    }
  };

  // Format transcript for copying to clipboard (plain text)
  const formatTranscriptForCopy = (transcriptData) => {
    if (!transcriptData) return '';
    
    // Handle string transcripts
    if (typeof transcriptData === 'string') {
      return transcriptData;
    }
    
    // Handle array with tool calls
    if (!Array.isArray(transcriptData)) return '';
    
    return transcriptData.map((item) => {
      const { role, content, name, arguments: args, successful, tool_call_id, digit, time_sec } = item;
      
      if (role === 'user' || role === 'agent') {
        return `${role === 'user' ? 'User' : 'Agent'}: ${content}`;
      }

      if (role === 'dtmf') {
        return `[DTMF] Caller pressed "${digit}"${time_sec != null ? ` at ${Number(time_sec).toFixed(2)}s` : ''}`;
      }
      
      if (role === 'tool_call_invocation') {
        let parsedArgs = {};
        try {
          parsedArgs = JSON.parse(args || '{}');
        } catch (e) {
          parsedArgs = { raw: args };
        }
        let result = `\n--- Tool Call: ${name} ---`;
        result += `\nTool ID: ${tool_call_id}`;
        if (parsedArgs.execution_message) {
          result += `\nMessage: ${parsedArgs.execution_message}`;
        }
        result += `\nArguments:\n${JSON.stringify(parsedArgs, null, 2)}`;
        return result;
      }
      
      if (role === 'tool_call_result') {
        let parsedContent = {};
        try {
          parsedContent = typeof content === 'string' ? JSON.parse(content) : content;
        } catch (e) {
          parsedContent = { raw: content };
        }
        let result = `\n--- Tool Result: ${successful ? 'Success' : 'Failed'} ---`;
        result += `\nTool ID: ${tool_call_id}`;
        result += `\nResult:\n${JSON.stringify(parsedContent, null, 2)}`;
        result += '\n---';
        return result;
      }
      
      return '';
    }).filter(Boolean).join('\n\n');
  };

  const [transcriptCopied, setTranscriptCopied] = useState(false);

  const copyTranscriptToClipboard = () => {
    if (!callDetails?.retellCall) return;
    
    const transcriptData = callDetails.retellCall.transcript_with_tool_calls || callDetails.retellCall.transcript;
    const transcriptText = formatTranscriptForCopy(transcriptData);
    const headerLine = `callId: ${callDetails.id}, practiceId: ${callDetails.practiceId}, callTs: ${callDetails.callTs || ''}`;
    const textToCopy = headerLine + '\n\n' + transcriptText;
    
    navigator.clipboard.writeText(textToCopy).then(() => {
      setTranscriptCopied(true);
      setTimeout(() => setTranscriptCopied(false), 2000);
    }).catch((err) => {
      console.error('Failed to copy transcript:', err);
    });
  };

  const formatTranscriptWithToolCalls = (transcriptArray) => {
    if (!transcriptArray || !Array.isArray(transcriptArray)) {
      return <div className="no-transcript">No transcript available</div>;
    }

    return transcriptArray.map((item, index) => {
      const { role, content, name, arguments: args, successful, tool_call_id, digit, time_sec } = item;

      // Handle regular user/agent messages
      if (role === 'user' || role === 'agent') {
        return (
          <div key={index} className={`transcript-line ${role}`}>
            <span className="transcript-speaker">{role === 'user' ? 'User' : 'Agent'}:</span>
            <span className="transcript-content">{content}</span>
          </div>
        );
      }

      // Handle DTMF keypad presses
      if (role === 'dtmf') {
        return (
          <div key={index} className="transcript-line dtmf">
            <span className="dtmf-icon">☎️</span>
            <span className="dtmf-label">Caller pressed</span>
            <span className="dtmf-digit">{digit}</span>
            {time_sec != null && (
              <span className="dtmf-time">at {Number(time_sec).toFixed(2)}s</span>
            )}
          </div>
        );
      }

      // Handle tool call invocations
      if (role === 'tool_call_invocation') {
        let parsedArgs = {};
        try {
          parsedArgs = JSON.parse(args || '{}');
        } catch (e) {
          parsedArgs = { raw: args };
        }

        return (
          <div key={index} className="transcript-line tool-call">
            <div className="tool-call-header">
              <span className="tool-icon">🔧</span>
              <span className="tool-name">{name}</span>
              <span className="tool-id">{tool_call_id}</span>
            </div>
            {parsedArgs.execution_message && (
              <div className="tool-execution-message">
                <strong>Message:</strong> {parsedArgs.execution_message}
              </div>
            )}
            <details className="tool-details">
              <summary>View Arguments</summary>
              <pre className="tool-arguments">{JSON.stringify(parsedArgs, null, 2)}</pre>
            </details>
          </div>
        );
      }

      // Handle tool call results
      if (role === 'tool_call_result') {
        let parsedContent = {};
        try {
          parsedContent = typeof content === 'string' ? JSON.parse(content) : content;
        } catch (e) {
          parsedContent = { raw: content };
        }

        return (
          <div key={index} className="transcript-line tool-result">
            <div className="tool-result-header">
              <span className="tool-icon">{successful ? '✅' : '❌'}</span>
              <span className="tool-status">{successful ? 'Success' : 'Failed'}</span>
              <span className="tool-id">{tool_call_id}</span>
            </div>
            <details className="tool-details">
              <summary>View Result</summary>
              <pre className="tool-result-content">{JSON.stringify(parsedContent, null, 2)}</pre>
            </details>
          </div>
        );
      }

      // Fallback for unknown role types
      return null;
    }).filter(Boolean);
  };

  const formatTranscript = (transcript) => {
    if (!transcript) return 'No transcript available';
    
    // Split by newlines and format each line
    return transcript.split('\n').map((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      
      // Determine if it's User or Agent
      const isUser = trimmed.startsWith('User:');
      const isAgent = trimmed.startsWith('Agent:');
      
      if (isUser || isAgent) {
        const speaker = isUser ? 'User' : 'Agent';
        const content = trimmed.substring(speaker.length + 1).trim();
        
        return (
          <div key={index} className={`transcript-line ${speaker.toLowerCase()}`}>
            <span className="transcript-speaker">{speaker}:</span>
            <span className="transcript-content">{content}</span>
          </div>
        );
      } else {
        // Continuation line (no speaker prefix)
        return (
          <div key={index} className="transcript-line continuation">
            <span className="transcript-content">{trimmed}</span>
          </div>
        );
      }
    }).filter(Boolean);
  };

  /* ---------- NEW: BLF management functions ---------- */
  const openBlfModal = async (extension) => {
    console.log('🔧 BLF Modal: Opening for extension', extension);
    if (!resolvedDomain) {
      alert('No domain resolved. Please search for a domain first.');
      return;
    }

    console.log('🔧 BLF Modal: Setting states...');
    setCurrentBlfExtension(extension);
    setBlfModalOpen(true);
    setBlfError('');
    console.log('🔧 BLF Modal: Modal should be open now');
    await fetchBlfData(extension);
  };

  const closeBlfModal = () => {
    setBlfModalOpen(false);
    setCurrentBlfExtension(null);
    setBlfData(null);
    setBlfButtons([]);
    setAvailableModes([]);
    setBlfError('');
  };

  const fetchBlfData = async (extension) => {
    setBlfLoading(true);
    setBlfError('');
    
    try {
      const response = await secureFetch(`${BASE_URL}/api/extensions/${resolvedDomain}/${extension.name}/blf`, {
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success && data.blf_data) {
        setBlfData(data.blf_data);
        
        console.log('Raw BLF data from API:', data.blf_data);
        
        // Parse the buttons JSON string
        let buttonsData = { buttons: [], modes: {} };
        try {
          if (data.blf_data.buttons) {
            if (typeof data.blf_data.buttons === 'string') {
              buttonsData = JSON.parse(data.blf_data.buttons);
            } else {
              buttonsData = data.blf_data.buttons;
            }
          }
        } catch (error) {
          console.error('Error parsing buttons JSON:', error);
          console.log('Raw buttons data:', data.blf_data.buttons);
        }
        
        const existingButtons = buttonsData.buttons || [];
        console.log('Parsed buttons data:', buttonsData);
        console.log('Existing buttons from API:', existingButtons);
        
        // Always ensure we have 10 buttons
        const normalizedButtons = [];
        for (let i = 0; i < 10; i++) {
          const buttonNumber = i + 1;
          // Try to find existing button by number, handling "linekey.X" format
          const existingButton = existingButtons.find(btn => {
            const isLinekeyMatch = btn.number === `linekey.${buttonNumber}`;
            const isDirectMatch = btn.number === buttonNumber || btn.number === buttonNumber.toString();
            
            if (isLinekeyMatch) {
              console.log(`✓ Found linekey match for button ${buttonNumber}: ${btn.number}`);
              return true;
            }
            if (isDirectMatch) {
              console.log(`✓ Found direct match for button ${buttonNumber}: ${btn.number}`);
              return true;
            }
            return false;
          });
          
          if (existingButton) {
            console.log(`Found existing button ${buttonNumber}:`, existingButton);
            normalizedButtons.push({
              number: buttonNumber.toString(),
              type: existingButton.type || 'none',
              parameter: existingButton.parameter || '',
              name: existingButton.name || ''
            });
          } else {
            console.log(`✗ No existing config found for button ${buttonNumber}, using default`);
            normalizedButtons.push({
              number: buttonNumber.toString(),
              type: 'extension',
              parameter: '',
              name: ''
            });
          }
        }
        
        console.log('Normalized buttons:', normalizedButtons);
        
        // Summary of what was loaded
        const configuredButtons = normalizedButtons.filter(btn => btn.type !== 'none');
        console.log(`📊 BLF Summary: ${configuredButtons.length} buttons configured out of 10 total`);
        configuredButtons.forEach(btn => {
          console.log(`  Button ${btn.number}: ${btn.type}${btn.parameter ? ` (${btn.parameter})` : ''}`);
        });
        
        setBlfButtons(normalizedButtons);
        
        // Extract available modes (button types)
        const modes = buttonsData.modes || {};
        const allModes = modes["1"] || [];
        console.log('Available modes from API:', allModes);
        
        // Ensure we have some default modes if none are provided
        const defaultModes = [
          'extension', 'private', 'speed', 'intercom', 'vm', 'dnd', 'page', 
          'hunt', 'queue', 'conference', 'park', 'dtmf', 'attendant', 'none'
        ];
        const finalModes = allModes.length > 0 ? allModes : defaultModes;
        
        console.log('Final modes to use:', finalModes);
        setAvailableModes(finalModes);
        
      } else {
        throw new Error(data.message || 'Failed to fetch BLF data');
      }
    } catch (error) {
      console.error('Error fetching BLF data:', error);
      setBlfError(`Failed to load BLF data: ${error.message}`);
    } finally {
      setBlfLoading(false);
    }
  };

  const updateBlfButton = (index, field, value) => {
    setBlfButtons(prev => prev.map((button, i) => {
      if (i === index) {
        const updatedButton = { ...button, [field]: value };
        
        // Clear parameter when switching to types that don't need parameters
        if (field === 'type') {
          const typesWithoutParams = ['private', 'vm', 'dnd', 'none'];
          if (typesWithoutParams.includes(value)) {
            updatedButton.parameter = '';
          }
        }
        
        return updatedButton;
      }
      return button;
    }));
  };

  const saveBlfChanges = async () => {
    if (!currentBlfExtension || !blfData) {
      alert('No BLF data to save');
      return;
    }

    setBlfSaving(true);
    setBlfError('');
    
    try {
      // Keep all buttons, but only save ones that have meaningful configuration
      const buttonsToSave = blfButtons.map((button, index) => ({
        number: `linekey.${button.number}`, // Convert back to linekey.X format
        name: "Line Key", // Standard name used in API
        type: button.type || 'none',
        parameter: button.parameter || '',
        sort: parseInt(button.number), // Add sort order
        group: 1 // Standard group value
      })).filter(button => 
        button.type && button.type !== 'none' && 
        (button.type !== 'extension' || button.parameter) // Only save extension buttons that have a parameter
      );

      console.log('Buttons to save:', buttonsToSave);

      // Reconstruct the buttons payload
      const buttonsPayload = {
        mac: blfData.mac,
        template: blfData.template || "0",
        buttons: JSON.stringify({
          buttons: buttonsToSave,
          modes: { "1": availableModes }
        })
      };

      console.log('Payload being sent:', buttonsPayload);

      const response = await secureFetch(
        `${BASE_URL}/api/extensions/${resolvedDomain}/${currentBlfExtension.name}/blf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true'
          },
          body: JSON.stringify(buttonsPayload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        alert('BLF configuration updated successfully and synced to phone');
        closeBlfModal();
      } else {
        throw new Error(result.message || 'Failed to update BLF configuration');
      }
    } catch (error) {
      console.error('Error updating BLF:', error);
      setBlfError(`Error: ${error.message}`);
    } finally {
      setBlfSaving(false);
    }
  };

  /* ---------- NEW: Ring Groups management functions ---------- */
  const fetchRingGroups = async (domain = null, practiceId = null) => {
    const targetDomain = domain || resolvedDomain;
    const targetPracticeId = practiceId || currentPracticeId;
    
    console.log('🔍 fetchRingGroups called with:', { 
      domain, 
      practiceId, 
      targetDomain, 
      targetPracticeId,
      currentPracticeId,
      searchPracticeId 
    });
    
    if (!targetDomain) {
      setRingGroupsError('Please search for a domain first');
      return;
    }

    setRingGroupsLoading(true);
    setRingGroupsError('');
    
    try {
      // Build URL with practice_id query parameter if available
      let url = `${BASE_URL}/api/ring-groups/${targetDomain}`;
      if (targetPracticeId) {
        url += `?practice_id=${encodeURIComponent(targetPracticeId)}`;
      }
      
      console.log('🌐 Ring Groups API URL:', url);
      
      const response = await secureFetch(url, {
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setRingGroups(data.ring_groups || []);
        setPhoneMaps(data.phone_maps || {});
        console.log(`Found ${data.count || 0} ring groups for ${targetDomain}`);
        console.log('Phone maps:', data.phone_maps);
      } else {
        throw new Error(data.message || 'Failed to fetch ring groups');
      }
    } catch (error) {
      console.error('Error fetching ring groups:', error);
      setRingGroupsError(`Failed to load ring groups: ${error.message}`);
      setRingGroups([]);
    } finally {
      setRingGroupsLoading(false);
    }
  };

  /* ---------- render ---------- */
  return (
    <div className="App">
      {activeTab !== 'form' && activeTab !== 'quoteBuilder' && activeTab !== 'orgRegistration' && activeTab !== 'winReport' && (
      <div className="tabs">
        {onSwitchToNew && (
          <button
            onClick={onSwitchToNew}
            style={{ background: '#1E6434', color: 'white', borderRadius: 6, marginRight: 10, padding: '6px 14px', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer' }}
          >
            → New Interface
          </button>
        )}
        <button
          className={activeTab === 'onboarding' ? 'active' : ''}
          onClick={() => setActiveTab('onboarding')}
        >
          Onboarding Users
        </button>
        <button
          className={activeTab === 'createOrg' ? 'active' : ''}
          onClick={() => setActiveTab('createOrg')}
        >
          Create Org & Admin
        </button>
        <button
          className={activeTab === 'demoCall' ? 'active' : ''}
          onClick={() => setActiveTab('demoCall')}
        >
          Schedule Demo Call
        </button>
        <button
        className={activeTab === 'recallStats' ? 'active' : ''}
        onClick={() => setActiveTab('recallStats')}
      >
        Recall Stats
      </button>
        <button
          className={activeTab === 'form' ? 'active' : ''}
          onClick={() => setActiveTab('form')}
        >
          Demo Form
        </button>
        <button
          className={activeTab === 'practicesForPort' ? 'active' : ''}
          onClick={() => setActiveTab('practicesForPort')}
        >
          Practices For Port
        </button>
        <button
          className={activeTab === 'simpleForm' ? 'active' : ''}
          onClick={() => setActiveTab('simpleForm')}
        >
          Add VoIP Config
        </button>
        <button
          className={activeTab === 'manageVoip' ? 'active' : ''}
          onClick={() => setActiveTab('manageVoip')}
        >
          Manage Extensions
        </button>
        <button
          className={activeTab === 'addExtensions' ? 'active' : ''}
          onClick={() => setActiveTab('addExtensions')}
        >
          Add Extensions
        </button>
        <button
          className={activeTab === 'pcapAnalysis' ? 'active' : ''}
          onClick={() => setActiveTab('pcapAnalysis')}
        >
          PCAP Analysis
        </button>
        <button
          className={activeTab === 'queryTool' ? 'active' : ''}
          onClick={() => setActiveTab('queryTool')}
        >
          Query Tool
        </button>
        <button
          className={activeTab === 'outboundAnalytics' ? 'active' : ''}
          onClick={() => setActiveTab('outboundAnalytics')}
        >
          Outbound Analytics
        </button>
        <button
          className={activeTab === 'callInsights' ? 'active' : ''}
          onClick={() => setActiveTab('callInsights')}
        >
          Call Insights
        </button>
        <button
          className={activeTab === 'callDetails' ? 'active' : ''}
          onClick={() => setActiveTab('callDetails')}
        >
          Call Details
        </button>
        <button
          className={activeTab === 'onboardingView' ? 'active' : ''}
          onClick={() => setActiveTab('onboardingView')}
        >
          Onboarding View
        </button>
        <button className={activeTab === 'locationOnboarding' ? 'active' : ''} onClick={() => setActiveTab('locationOnboarding')}>Location Onboarding</button>
        
        <button
          className={activeTab === 'demoCalls' ? 'active' : ''}
          onClick={() => setActiveTab('demoCalls')}
        >
          Demo Calls
        </button>
        <button
          className={activeTab === 'salesTouchpoints' ? 'active' : ''}
          onClick={() => setActiveTab('salesTouchpoints')}
        >
          Sales Touchpoints
        </button>
        {/* ARR moved to New Interface */}
      </div>)}

      {/* ---------- ONBOARDING TAB ---------- */}
      {activeTab === 'locationOnboarding' && <LocationOnboarding />}
      {activeTab === 'onboarding' && (
        <div className="tab-content">
          {/* registration email block */}
          <div className="registration-section">
            <h2>Send Registration Email</h2>
            <div className="form-row">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
              />
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
              />
            </div>
            <div className="form-row">
              <input
                type="text"
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
                placeholder="Organization ID"
              />
              <input
                type="email"
                value={registrationEmail}
                onChange={(e) => setRegistrationEmail(e.target.value)}
                placeholder="Enter email address"
              />
            </div>
            
            {/* Practice IDs with add/remove buttons */}
            <div style={{ marginTop: '10px' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                Practice IDs:
              </label>
              {practiceIds.map((practiceId, index) => (
                <div key={index} className="form-row" style={{ marginBottom: '5px', display: 'flex', gap: '5px' }}>
                  <input
                    type="text"
                    value={practiceId}
                    onChange={(e) => updatePracticeId(index, e.target.value)}
                    placeholder={`Practice ID ${index + 1}`}
                    style={{ flex: 1 }}
                  />
                  {practiceIds.length > 1 && (
                    <button 
                      type="button"
                      onClick={() => removePracticeIdField(index)}
                      style={{ 
                        padding: '5px 10px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button 
                type="button"
                onClick={addPracticeIdField}
                style={{ 
                  marginTop: '5px',
                  padding: '5px 10px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                + Add Another Practice
              </button>
            </div>

            <div className="form-row">
              <select
                value={isAdminUser.toString()}
                onChange={(e) => setIsAdminUser(e.target.value === 'true')}
              >
                <option value="true">Admin User</option>
                <option value="false">Regular User</option>
              </select>
             <div className="checkbox-container">
               <input
                 type="checkbox"
                 id="nonVoipOnboardingEmail"
                 checked={nonVoipOnboarding}
                 onChange={(e) => setNonVoipOnboarding(e.target.checked)}
               />
               <label htmlFor="nonVoipOnboardingEmail">Non-VoIP Onboarding</label>
             </div>
              <button onClick={sendRegistrationEmail}>Send Registration Email</button>
            </div>
            {emailStatus && <div className="status-message">{emailStatus}</div>}
          </div>

          {/* registration link block */}
          <div className="registration-link-section">
            <h2>Generate Registration Link</h2>
            <div className="form-row">
              <input
                type="email"
                value={linkEmail}
                onChange={(e) => setLinkEmail(e.target.value)}
                placeholder="Enter email address"
              />
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="nonVoipOnboarding"
                  checked={nonVoipOnboarding}
                  onChange={(e) => setNonVoipOnboarding(e.target.checked)}
                />
                <label htmlFor="nonVoipOnboarding">Non-VoIP Onboarding</label>
              </div>
              <button onClick={generateRegistrationLink}>Generate Link</button>
            </div>
            {registrationLink && (
              <div className="registration-link">{registrationLink}</div>
            )}
          </div>

          {/* pre-generated org registration block */}
          <div className="registration-section">
            <h2>Pre-Generated Org Registration</h2>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
              Enter your email address to send yourself an account regsitration link for the entered org id. Your account will have admin permissions
            </p>
            <div className="form-row">
              <input
                type="email"
                value={orgRegEmail}
                onChange={(e) => setOrgRegEmail(e.target.value)}
                placeholder="Email address"
              />
              <input
                type="text"
                value={orgRegOrgId}
                onChange={(e) => setOrgRegOrgId(e.target.value)}
                placeholder="Organization ID"
              />
              <button onClick={sendOrgRegistration}>Send Registration</button>
            </div>
            {orgRegStatus && <div className="status-message">{orgRegStatus}</div>}
          </div>

          {/* onboarding users table */}
          <h1>Onboarding Users</h1>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Stage</th>
                <th>Company</th>
                <th>Practice</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    {user.firstName} {user.lastName}
                  </td>
                  <td>{user.email}</td>
                  <td>{getStageLabel(user.currentOnboardingState)}</td>
                  <td>{user.companyName}</td>
                  <td>{user.practiceName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---------- RECALL STATS TAB ---------- */}
      {activeTab === 'recallStats' && (
        <div className="tab-content">
          <h1>Recall Stats - All Practices</h1>
          
          {/* Period selector */}
          <div className="recall-controls">
            <label htmlFor="period">Time Period:</label>
            <select
              id="period"
              value={recallPeriod}
              onChange={(e) => setRecallPeriod(e.target.value)}
            >
              <option value="today">Today</option>
              <option value="week">Past Week</option>
              <option value="month">Past Month</option>
              <option value="all">All Time</option>
            </select>
            
            <button onClick={fetchRecallStats} disabled={recallLoading}>
              {recallLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          
          {/* Practice filter */}
          <div className="filter-section">
            <input
              type="text"
              placeholder="Filter by practice ID..."
              value={practiceFilter}
              onChange={(e) => setPracticeFilter(e.target.value)}
            />
          </div>
          
          {/* Error message */}
          {recallError && (
            <div className="error-message">{recallError}</div>
          )}
          
          {/* Summary stats */}
          {recallStats && recallStats.summary && (
            <div className="recall-summary">
              <h2>Summary</h2>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Total Practices:</span>
                  <span className="stat-value">{recallStats.summary.totalPractices}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Reached Out:</span>
                  <span className="stat-value">{recallStats.summary.totalReachedOut}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Bookings:</span>
                  <span className="stat-value">{recallStats.summary.totalBookingsMade}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Waitlisted:</span>
                  <span className="stat-value">{recallStats.summary.totalAddedToWaitlist}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Practices table */}
          {recallStats && (
            <div className="practices-section">
              <h2>Practices ({getFilteredPractices().length})</h2>
              <table>
                <thead>
                  <tr>
                    <th>Practice ID</th>
                    <th>Reached Out</th>
                    <th>Bookings Made</th>
                    <th>Added to Waitlist</th>
                    <th>Total Activity</th>
                    <th>Outbound Outreach (30d)</th>
                    <th>Outbound Bookings (30d)</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredPractices().map((practice) => {
                    const analytics = practice.analytics;
                    const total = analytics.reachedOut + analytics.bookingsMade + analytics.addedToWaitlist;
                    const ob = outboundStats[practice.practiceId];
                    
                    return (
                      <tr key={practice.practiceId}>
                        <td>{practice.practiceId}</td>
                        <td>{analytics.reachedOut}</td>
                        <td>{analytics.bookingsMade}</td>
                        <td>{analytics.addedToWaitlist}</td>
                        <td><strong>{total}</strong></td>
                        <td>{ob ? ob.outreach : '—'}</td>
                        <td>{ob ? ob.bookings : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
        </div>
      )}

      {/* ---------- CREATE ORG & ADMIN TAB ---------- */}
      {activeTab === 'createOrg' && (
        <div className="tab-content">
          <CreateOrgAndAdmin />
        </div>
      )}

      {/* ---------- QUOTE BUILDER (standalone route /quoteBuilder) ---------- */}
      {activeTab === 'quoteBuilder' && (
        <div className="tab-content">
          <QuoteBuilder />
        </div>
      )}

      {/* ---------- ORG REGISTRATION (standalone route /orgRegistration) ---------- */}
      {activeTab === 'orgRegistration' && (
        <div className="tab-content">
          <OrgRegistration />
        </div>
      )}

      {/* ---------- WIN REPORT (standalone route /winReport?quoteId=xxx) ---------- */}
      {activeTab === 'winReport' && (
        <div className="tab-content">
          <WinReport quoteId={new URLSearchParams(window.location.search).get('quoteId')} />
        </div>
      )}

      {/* ---------- DEMO CALL TAB ---------- */}
      {activeTab === 'demoCall' && (
        <div className="tab-content">
          <h1>Schedule Demo Phone Call</h1>
          <div className="call-form">
            <div className="form-group">
              <label htmlFor="patientName">Patient Name:</label>
              <input
                type="text"
                id="patientName"
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
                placeholder="Enter patient name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number:</label>
              <input
                type="text"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter 10-digit phone number"
              />
            </div>

            <button onClick={scheduleDemo} className="call-button">
              Schedule Call
            </button>

            {callStatus && <div className="status-message">{callStatus}</div>}
          </div>
        </div>
      )}
      {activeTab === 'form' && <DemoForm />}
      {activeTab === 'practicesForPort' && <PracticesForPort />}
      {activeTab === 'queryTool' && <QueryTool />}
      {activeTab === 'outboundAnalytics' && <OutboundAnalytics />}
      {activeTab === 'callInsights' && <CallInsights />}
      
      {/* ---------- VOIP CONFIGURATION TAB ---------- */}
      {activeTab === 'simpleForm' && <VoIPConfiguration />}

      {/* ---------- ADD EXTENSIONS TAB ---------- */}
      {activeTab === 'addExtensions' && <AddExtensions />}

      {/* ---------- MANAGE VOIP CONFIGS TAB ---------- */}
      {activeTab === 'manageVoip' && (
        <div className="tab-content">
          <h1>Manage VoIP Extensions</h1>
          
          {/* Practice ID search and lookup */}
          <div className="domain-controls">
            <div className="form-group">
              <label htmlFor="search-input">Practice ID:</label>
              <input
                type="text"
                id="search-input"
                value={searchPracticeId}
                onChange={(e) => setSearchPracticeId(e.target.value)}
                placeholder="Enter practice ID (e.g., practice-1747851038316)"
                className="domain-input"
                onKeyPress={(e) => e.key === 'Enter' && lookupDomain()}
              />
            </div>
            <button 
              onClick={lookupDomain} 
              disabled={domainLoading || extensionsLoading}
              className="load-extensions-btn"
            >
              {domainLoading ? 'Looking up...' : extensionsLoading ? 'Loading Extensions...' : 'Search & Load'}
            </button>
            {resolvedDomain && (
              <button 
                onClick={fetchExtensions} 
                disabled={extensionsLoading}
                className="refresh-extensions-btn"
              >
                {extensionsLoading ? 'Loading...' : 'Refresh'}
              </button>
            )}
          </div>
          
          {/* Show resolved domain info */}
          {resolvedDomain && (
            <div className="resolved-domain-info">
              <div className="domain-info-item">
                <span className="domain-label">Practice ID:</span>
                <span className="domain-value">{currentPracticeId}</span>
              </div>
              <div className="domain-info-item">
                <span className="domain-label">Domain:</span>
                <span className="domain-value">{resolvedDomain}</span>
              </div>
            </div>
          )}
          
          {/* Error message */}
          {extensionsError && (
            <div className="error-message">{extensionsError}</div>
          )}
          
          {/* Extensions table */}
          {extensions.length > 0 && (
            <div className="extensions-section">
              <h2>Extensions for {currentDisplayName} ({extensions.length})</h2>
              <div className="extensions-table-container">
                <table className="extensions-table">
                  <thead>
                    <tr>
                      <th>Extension</th>
                      <th>Display Name</th>
                      <th>ANI</th>
                      <th>MAC Address</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extensions.map((extension) => (
                      <tr key={extension.name}>
                        <td>
                          <strong>{extension.name}</strong>
                        </td>
                        <td>
                          {editingExtension?.name === extension.name ? (
                            <div className="edit-display-name">
                              <input
                                type="text"
                                value={newDisplayName}
                                onChange={(e) => setNewDisplayName(e.target.value)}
                                className="rename-input"
                                placeholder="Enter display name"
                              />
                              <div className="edit-actions">
                                <button 
                                  onClick={saveRename}
                                  disabled={renameLoading}
                                  className="save-btn"
                                >
                                  {renameLoading ? 'Saving...' : 'Save'}
                                </button>
                                <button 
                                  onClick={cancelRename}
                                  disabled={renameLoading}
                                  className="cancel-btn"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <span className="display-name">
                              {extension.display_name || 'Not set'}
                            </span>
                          )}
                        </td>
                        <td>{extension.ani || 'N/A'}</td>
                        <td>
                          {extension.macs && extension.macs.length > 0 
                            ? extension.macs.join(', ')
                            : 'Softphone'
                          }
                        </td>
                        <td>
                          <span className={`status ${extension.regs > 0 ? 'online' : 'offline'}`}>
                            {extension.regs > 0 ? 'Online' : 'Offline'}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            {editingExtension?.name === extension.name ? (
                              <span className="editing-indicator">Editing...</span>
                            ) : (
                              <>
                                <button 
                                  onClick={() => startRenameExtension(extension)}
                                  className="rename-btn"
                                  disabled={editingExtension !== null}
                                >
                                  Rename
                                </button>
                                <button 
                                  onClick={() => openBlfModal(extension)}
                                  className="blf-btn"
                                  disabled={editingExtension !== null}
                                >
                                  BLF
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* No extensions message */}
          {!extensionsLoading && extensions.length === 0 && resolvedDomain && !extensionsError && (
            <div className="no-extensions">
              <p>No extensions found for "{currentDisplayName}" ({resolvedDomain}). The domain may not have any configured extensions.</p>
            </div>
          )}

          {/* Ring Groups table */}
          {resolvedDomain && (
            <div className="ring-groups-section">
              <h2>Ring Groups for {currentDisplayName} ({ringGroups.length})</h2>
              
              {ringGroupsError && (
                <div className="error-message">{ringGroupsError}</div>
              )}
              
              {ringGroups.length > 0 && (
                <div className="ring-groups-table-container">
                  <table className="ring-groups-table">
                    <thead>
                      <tr>
                        <th>Phone Numbers</th>
                        <th>Name</th>
                        <th>Display Name</th>
                        <th>Agents</th>
                        <th>Active Calls</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ringGroups.slice().reverse().map((ringGroup, index) => {
                        const phoneNumbers = getPhoneNumbersForRingGroup(ringGroup.name);
                        return (
                          <tr key={ringGroup.name || index}>
                            <td>
                              <div className="phone-numbers-list">
                                {phoneNumbers.length > 0 ? (
                                  phoneNumbers.map((phoneNumber, idx) => (
                                    <span key={idx} className="phone-number-tag">
                                      {phoneNumber}
                                    </span>
                                  ))
                                ) : (
                                  <span className="no-phone-numbers">No numbers</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <strong>{ringGroup.name}</strong>
                            </td>
                            <td>{ringGroup.display || 'Not set'}</td>
                            <td>
                              {editingRingGroup?.name === ringGroup.name ? (
                                <div className="edit-agents">
                                  <div className="available-extensions">
                                    <div className="extensions-grid">
                                      {extensions.map((ext) => (
                                        <div key={ext.name} className="extension-option">
                                          <input
                                            type="checkbox"
                                            id={`agent_${ringGroup.name}_${ext.name}`}
                                            checked={selectedAgents.includes(ext.name)}
                                            onChange={() => toggleAgentSelection(ext.name)}
                                          />
                                          <label htmlFor={`agent_${ringGroup.name}_${ext.name}`}>
                                            Ext {ext.name} ({ext.display_name || 'No Name'})
                                          </label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="edit-actions">
                                    <button 
                                      onClick={saveRingGroupAgents}
                                      disabled={ringGroupSaving}
                                      className="save-btn"
                                    >
                                      {ringGroupSaving ? 'Saving...' : 'Save'}
                                    </button>
                                    <button 
                                      onClick={cancelEditingRingGroup}
                                      disabled={ringGroupSaving}
                                      className="cancel-btn"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="agents-list">
                                  {ringGroup.agents && ringGroup.agents.length > 0 ? (
                                    ringGroup.agents.map((agent, idx) => (
                                      <span key={idx} className="agent-tag">
                                        Ext {agent}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="no-agents">No agents</span>
                                  )}
                                </div>
                              )}
                            </td>
                            <td>
                              <span className={`calls-count ${ringGroup.calls > 0 ? 'active' : 'idle'}`}>
                                {ringGroup.calls || 0}
                              </span>
                            </td>
                            <td>
                              <div className="ring-group-actions">
                                {editingRingGroup?.name === ringGroup.name ? (
                                  <span className="editing-indicator">Editing...</span>
                                ) : (
                                  <button 
                                    onClick={() => startEditingRingGroup(ringGroup)}
                                    className="edit-agents-btn"
                                    disabled={editingRingGroup !== null}
                                  >
                                    Edit Agents
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              
              {!ringGroupsLoading && ringGroups.length === 0 && !ringGroupsError && (
                <div className="no-ring-groups">
                  <p>No ring groups found for "{currentDisplayName}" ({resolvedDomain}). The domain may not have any configured ring groups.</p>
                </div>
              )}
              
              {ringGroupsLoading && (
                <div className="loading-indicator">
                  Loading ring groups...
                </div>
              )}
            </div>
          )}
          
          {/* Instructions */}
          {!resolvedDomain && (
            <div className="instructions">
              <h3>Instructions:</h3>
              <ol>
                <li>Enter the practice ID (e.g., practice-1747851038316)</li>
                <li>Click "Search & Load" to lookup the domain and fetch extensions</li>
                <li>Use the "Rename" button to change the display name of any extension</li>
                <li>Changes are saved immediately and sync to all phones</li>
                <li>Use "Refresh" to reload extensions after making changes</li>
              </ol>
            </div>
          )}
          
          {/* BLF Management Modal */}
          {blfModalOpen && currentBlfExtension && (
            <div className="blf-modal-overlay" onClick={closeBlfModal}>
              <div className="blf-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>BLF Configuration - Extension {currentBlfExtension.name}</h2>
                  <button onClick={closeBlfModal} className="close-btn">×</button>
                </div>
                
                <div className="modal-content">
                  {blfLoading && (
                    <div className="loading-indicator">
                      Loading BLF configuration...
                    </div>
                  )}
                  
                  {blfError && (
                    <div className="error-message">{blfError}</div>
                  )}
                  
                  {blfData && (
                    <div className="blf-config-section">
                      <div className="device-info">
                        <h3>Device Information</h3>
                        <div className="device-details">
                          <div className="device-item">
                            <span className="device-label">MAC Address:</span>
                            <span className="device-value">{blfData.mac || 'N/A'}</span>
                          </div>
                          <div className="device-item">
                            <span className="device-label">Vendor:</span>
                            <span className="device-value">{blfData.vendor || 'N/A'}</span>
                          </div>
                          <div className="device-item">
                            <span className="device-label">Model:</span>
                            <span className="device-value">{blfData.model || 'N/A'}</span>
                          </div>
                          <div className="device-item">
                            <span className="device-label">Version:</span>
                            <span className="device-value">{blfData.version || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="buttons-config">
                        <h3>Button Configuration (First 10 Buttons)</h3>
                        <div className="buttons-table-container">
                          <table className="buttons-table">
                            <thead>
                              <tr>
                                <th>Button</th>
                                <th>Type</th>
                                <th>Parameter</th>
                              </tr>
                            </thead>
                            <tbody>
                              {blfButtons.map((button, index) => (
                                <tr key={button.number || index}>
                                  <td>
                                    <strong>{button.number}</strong>
                                  </td>
                                  <td>
                                    <select
                                      value={button.type || 'none'}
                                      onChange={(e) => updateBlfButton(index, 'type', e.target.value)}
                                      className="type-select"
                                    >
                                      {availableModes.map(mode => (
                                        <option key={mode} value={mode}>
                                          {mode.charAt(0).toUpperCase() + mode.slice(1)}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td>
                                    {button.type === 'extension' ? (
                                      <select
                                        value={button.parameter || ''}
                                        onChange={(e) => updateBlfButton(index, 'parameter', e.target.value)}
                                        className="parameter-input"
                                      >
                                        <option value="">Select Extension</option>
                                        {extensions.length > 0 ? (
                                          extensions.map((ext) => (
                                            <option key={ext.name} value={ext.name}>
                                              {ext.name} - {ext.display_name || 'No Name'}
                                            </option>
                                          ))
                                        ) : (
                                          <option disabled>No extensions loaded</option>
                                        )}
                                      </select>
                                    ) : (
                                      <input
                                        type="text"
                                        value={button.parameter || ''}
                                        onChange={(e) => updateBlfButton(index, 'parameter', e.target.value)}
                                        placeholder={
                                          button.type === 'speed' ? 'Phone number (e.g., +1234567890)' :
                                          button.type === 'intercom' ? 'Extension number' :
                                          button.type === 'page' ? 'Page group number' :
                                          button.type === 'hunt' ? 'Hunt group number' :
                                          button.type === 'queue' ? 'Queue number' :
                                          button.type === 'dtmf' ? 'DTMF sequence' :
                                          'Parameter'
                                        }
                                        className="parameter-input"
                                        disabled={!['speed', 'dtmf', 'page', 'intercom', 'hunt', 'queue', 'conference', 'park'].includes(button.type)}
                                      />
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        <div className="button-help">
                          <h4>Button Type Descriptions:</h4>
                          <ul>
                            <li><strong>extension:</strong> Speed dial to extension (default - select from dropdown)</li>
                            <li><strong>private:</strong> Line for calls (no parameter needed)</li>
                            <li><strong>speed:</strong> Speed dial (parameter: phone number)</li>
                            <li><strong>intercom:</strong> Intercom call (parameter: extension number)</li>
                            <li><strong>vm:</strong> Voicemail access (no parameter needed)</li>
                            <li><strong>dnd:</strong> Do Not Disturb toggle (no parameter needed)</li>
                            <li><strong>page:</strong> Page group (parameter: page group number)</li>
                            <li><strong>hunt:</strong> Hunt group (parameter: hunt group number)</li>
                            <li><strong>queue:</strong> Call queue (parameter: queue number)</li>
                            <li><strong>none:</strong> Disabled button</li>
                          </ul>
                        </div>
                      </div>
                      
                      <div className="modal-actions">
                        <button 
                          onClick={saveBlfChanges}
                          disabled={blfSaving}
                          className="save-blf-btn"
                        >
                          {blfSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button 
                          onClick={closeBlfModal}
                          disabled={blfSaving}
                          className="cancel-blf-btn"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* ---------- PCAP ANALYSIS TAB ---------- */}
      {activeTab === 'pcapAnalysis' && (
        <div className="tab-content">
          <h1>PCAP Analysis Dashboard</h1>
          
          {/* Summary Statistics */}
          {pcapSummary && (
            <div className="pcap-summary">
              <h2>Summary Statistics</h2>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Total Analyses:</span>
                  <span className="stat-value">{pcapSummary.total_analyses || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Calls with Problems:</span>
                  <span className="stat-value">{pcapSummary.calls_with_problems || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Problem Rate:</span>
                  <span className="stat-value">{pcapSummary.problem_percentage?.toFixed(1) || 0}%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Avg Packet Loss:</span>
                  <span className="stat-value">{pcapSummary.avg_packet_loss?.toFixed(2) || 0}%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Avg Max Jitter:</span>
                  <span className="stat-value">{pcapSummary.avg_jitter?.toFixed(2) || 0}ms</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Filters Section */}
          <div className="pcap-filters">
            <h3>Filters</h3>
            <div className="filter-row">
              <div className="filter-group">
                <label htmlFor="call-id-filter">Call ID:</label>
                <input
                  type="text"
                  id="call-id-filter"
                  value={pcapFilters.call_id}
                  onChange={(e) => handlePcapFilterChange('call_id', e.target.value)}
                  placeholder="Enter call ID"
                />
              </div>
              
              <div className="filter-group">
                <label htmlFor="problems-filter">Has Problems:</label>
                <select
                  id="problems-filter"
                  value={pcapFilters.has_problems}
                  onChange={(e) => handlePcapFilterChange('has_problems', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label htmlFor="start-date-filter">Start Date:</label>
                <input
                  type="datetime-local"
                  id="start-date-filter"
                  value={pcapFilters.start_date}
                  onChange={(e) => handlePcapFilterChange('start_date', e.target.value)}
                />
              </div>
              
              <div className="filter-group">
                <label htmlFor="end-date-filter">End Date:</label>
                <input
                  type="datetime-local"
                  id="end-date-filter"
                  value={pcapFilters.end_date}
                  onChange={(e) => handlePcapFilterChange('end_date', e.target.value)}
                />
              </div>
              
              <div className="filter-group">
                <label htmlFor="sort-filter">Sort By:</label>
                <select
                  id="sort-filter"
                  value={pcapFilters.order_by}
                  onChange={(e) => handlePcapFilterChange('order_by', e.target.value)}
                >
                  <option value="analysis_timestamp">Analysis Time</option>
                  <option value="call_id">Call ID</option>
                  <option value="has_problems">Problems</option>
                  <option value="call_duration">Duration</option>
                  <option value="stream_count">Stream Count</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label htmlFor="direction-filter">Direction:</label>
                <select
                  id="direction-filter"
                  value={pcapFilters.order_direction}
                  onChange={(e) => handlePcapFilterChange('order_direction', e.target.value)}
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>
            
            <div className="filter-actions">
              <button onClick={applyPcapFilters} disabled={pcapLoading}>
                Apply Filters
              </button>
              <button onClick={clearPcapFilters} disabled={pcapLoading}>
                Clear Filters
              </button>
              <button onClick={() => fetchPcapAnalyses(pcapPagination.page, pcapFilters)} disabled={pcapLoading}>
                {pcapLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
          
          {/* Error Message */}
          {pcapError && (
            <div className="error-message">{pcapError}</div>
          )}
          
          {/* Data Table */}
          <div className="pcap-analyses-section">
            <h2>
              PCAP Analyses 
              {pcapPagination.total_records > 0 && (
                <span className="count-badge">
                  ({pcapPagination.total_records} total)
                </span>
              )}
            </h2>
            
            {pcapLoading && (
              <div className="loading-indicator">
                Loading PCAP analyses...
              </div>
            )}
            
            {!pcapLoading && pcapAnalyses.length > 0 && (
              <div className="pcap-table-container">
                <table className="pcap-table">
                                      <thead>
                      <tr>
                        <th>Call ID</th>
                        <th>Analysis Time</th>
                        <th>Caller → Callee</th>
                        <th>Call Start Time</th>
                        <th>Duration</th>
                        <th>Streams</th>
                        <th>Problems</th>
                        <th>Packet Loss</th>
                        <th>Max Jitter</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                  <tbody>
                    {pcapAnalyses.map((analysis) => (
                      <tr key={analysis.id}>
                        <td>
                          <strong>{analysis.call_id}</strong>
                        </td>
                        <td>{formatTimestamp(analysis.analysis_timestamp)}</td>
                        <td>
                          {analysis.caller_number && analysis.callee_number ? (
                            <span>
                              {formatPhoneDisplay(analysis.caller_number)} → {formatPhoneDisplay(analysis.callee_number)}
                            </span>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td>{formatTimestamp(analysis.call_start_time)}</td>
                        <td>
                          {(() => {
                            const duration = analysis.call_duration || calculateCallDuration(analysis.call_start_time, analysis.call_end_time);
                            return duration ? `${duration}s` : 'N/A';
                          })()}
                        </td>
                        <td>{analysis.stream_count || 0}</td>
                        <td>
                          <span className={`problems-status ${analysis.has_problems ? 'has-problems' : 'no-problems'}`}>
                            {analysis.has_problems ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          {analysis.aggregated_stats?.packet_loss_percentage?.toFixed(2) || 0}%
                        </td>
                        <td>
                          {analysis.aggregated_stats?.max_jitter_ms?.toFixed(2) || 0}ms
                        </td>
                        <td>
                          <button 
                            onClick={() => fetchAnalysisById(analysis.id)}
                            className="view-details-btn"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {pcapPagination.total_pages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => goToPcapPage(pcapPagination.page - 1)}
                  disabled={!pcapPagination.has_previous || pcapLoading}
                  className="pagination-btn"
                >
                  Previous
                </button>
                
                <span className="pagination-info">
                  Page {pcapPagination.page} of {pcapPagination.total_pages}
                  {pcapPagination.total_records > 0 && (
                    <span> ({pcapPagination.total_records} total records)</span>
                  )}
                </span>
                
                <button 
                  onClick={() => goToPcapPage(pcapPagination.page + 1)}
                  disabled={!pcapPagination.has_next || pcapLoading}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
            
            {/* No Data Message */}
            {!pcapLoading && pcapAnalyses.length === 0 && !pcapError && (
              <div className="no-data">
                <p>No PCAP analyses found. Data is populated automatically when wireshark.py runs periodically.</p>
              </div>
            )}
          </div>
          
          {/* Analysis Details Modal */}
          {analysisModalOpen && selectedAnalysis && (
            <div className="analysis-modal-overlay" onClick={closeAnalysisModal}>
              <div className="analysis-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>PCAP Analysis Details - Call {selectedAnalysis.call_id}</h2>
                  <button onClick={closeAnalysisModal} className="close-btn">×</button>
                </div>
                
                <div className="modal-content">
                  <div className="analysis-details">
                    {/* Call Information */}
                    <div className="detail-section">
                      <h3>Call Information</h3>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <span className="detail-label">Call ID:</span>
                          <span className="detail-value">{selectedAnalysis.call_id}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Analysis Time:</span>
                          <span className="detail-value">{formatTimestamp(selectedAnalysis.analysis_timestamp)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Caller:</span>
                          <span className="detail-value">{formatPhoneDisplay(selectedAnalysis.caller_number)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Callee:</span>
                          <span className="detail-value">{formatPhoneDisplay(selectedAnalysis.callee_number)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Duration:</span>
                          <span className="detail-value">
                            {(() => {
                              const duration = selectedAnalysis.call_duration || calculateCallDuration(selectedAnalysis.call_start_time, selectedAnalysis.call_end_time);
                              return duration ? `${duration}s` : 'N/A';
                            })()}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Start Time:</span>
                          <span className="detail-value">{formatTimestamp(selectedAnalysis.call_start_time)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">End Time:</span>
                          <span className="detail-value">{formatTimestamp(selectedAnalysis.call_end_time)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Analysis Summary */}
                    <div className="detail-section">
                      <h3>Analysis Summary</h3>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <span className="detail-label">Stream Count:</span>
                          <span className="detail-value">{selectedAnalysis.stream_count || 0}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Has Problems:</span>
                          <span className={`detail-value ${selectedAnalysis.has_problems ? 'problems-yes' : 'problems-no'}`}>
                            {selectedAnalysis.has_problems ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">PCAP File:</span>
                          <span className="detail-value">{selectedAnalysis.pcap_file_path}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Aggregated Statistics */}
                    {selectedAnalysis.aggregated_stats && (
                      <div className="detail-section">
                        <h3>Aggregated Statistics</h3>
                        <div className="detail-grid">
                          <div className="detail-item">
                            <span className="detail-label">Total Packets:</span>
                            <span className="detail-value">{selectedAnalysis.aggregated_stats.total_packets || 0}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Packet Loss:</span>
                            <span className="detail-value">{selectedAnalysis.aggregated_stats.total_packet_loss || 0}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Packet Loss %:</span>
                            <span className="detail-value">{selectedAnalysis.aggregated_stats.packet_loss_percentage?.toFixed(2) || 0}%</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Max Jitter:</span>
                            <span className="detail-value">{selectedAnalysis.aggregated_stats.max_jitter_ms?.toFixed(2) || 0}ms</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Mean Jitter:</span>
                            <span className="detail-value">{selectedAnalysis.aggregated_stats.mean_jitter_ms?.toFixed(2) || 0}ms</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* RTP Streams */}
                    {selectedAnalysis.rtp_summary && selectedAnalysis.rtp_summary.length > 0 && (
                      <div className="detail-section">
                        <h3>RTP Streams ({selectedAnalysis.rtp_summary.length})</h3>
                        <div className="rtp-table-container">
                          <table className="rtp-table">
                            <thead>
                              <tr>
                                <th>Source</th>
                                <th>Destination</th>
                                <th>Codec</th>
                                <th>Packets</th>
                                <th>Loss</th>
                                <th>Mean Jitter</th>
                                <th>Max Jitter</th>
                                <th>Problems</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedAnalysis.rtp_summary.map((stream, index) => (
                                <tr key={index}>
                                  <td>{stream.src_ip}:{stream.src_port}</td>
                                  <td>{stream.dst_ip}:{stream.dst_port}</td>
                                  <td>{stream.codec}</td>
                                  <td>{stream.packets}</td>
                                  <td>{stream.packet_loss}</td>
                                  <td>{stream.mean_jitter_ms?.toFixed(2)}ms</td>
                                  <td>{stream.max_jitter_ms?.toFixed(2)}ms</td>
                                  <td>
                                    <span className={`problems-status ${stream.problems ? 'has-problems' : 'no-problems'}`}>
                                      {stream.problems ? 'Yes' : 'No'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* CDR Information */}
                    {selectedAnalysis.cdr && (
                      <div className="detail-section">
                        <h3>CDR Information</h3>
                        <div className="cdr-details">
                          <pre>{JSON.stringify(selectedAnalysis.cdr, null, 2)}</pre>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="modal-actions">
                    <button onClick={closeAnalysisModal} className="close-modal-btn">
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---------- CALL DETAILS TAB ---------- */}
      {activeTab === 'callDetails' && (
        <div className="tab-content">
          <h1>Call Details</h1>
          
          {/* Call ID Search */}
          <div className="call-search-section">
            <div className="form-group">
              <label htmlFor="call-id-search">Call ID:</label>
              <input
                type="text"
                id="call-id-search"
                value={searchCallId}
                onChange={(e) => setSearchCallId(e.target.value)}
                placeholder="Enter call ID to search"
                className="call-id-input"
                onKeyPress={(e) => e.key === 'Enter' && fetchCallDetails()}
              />
            </div>
            <div className="call-search-actions">
              <button 
                onClick={fetchCallDetails} 
                disabled={callDetailsLoading}
                className="search-call-btn"
              >
                {callDetailsLoading ? 'Loading...' : 'Search Call'}
              </button>
              <button 
                onClick={clearCallDetails} 
                disabled={callDetailsLoading}
                className="clear-call-btn"
              >
                Clear
              </button>
            </div>
          </div>
          
          {/* Error Message */}
          {callDetailsError && (
            <div className="error-message">{callDetailsError}</div>
          )}
          
          {/* Transcript Section - Moved to top */}
          {callDetails && callDetails.retellCall && (callDetails.retellCall.transcript_with_tool_calls || callDetails.retellCall.transcript) && (
            <div className="transcript-section">
              <div className="transcript-header">
                <h2>Call Transcript</h2>
                <button 
                  onClick={copyTranscriptToClipboard}
                  className="copy-transcript-btn"
                  title="Copy entire conversation"
                >
                  {transcriptCopied ? '✓ Copied!' : '📋 Copy All'}
                </button>
              </div>
              <div className="transcript-container">
                {callDetails.retellCall.transcript_with_tool_calls 
                  ? formatTranscriptWithToolCalls(callDetails.retellCall.transcript_with_tool_calls)
                  : formatTranscript(callDetails.retellCall.transcript)
                }
              </div>
            </div>
          )}
          
          {/* Recording Section */}
          {callDetails && (
            <div className="recording-section" style={{ marginBottom: '20px' }}>
              <h2>Call Recording</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <button 
                  onClick={fetchRecording} 
                  disabled={recordingLoading}
                  className="search-call-btn"
                  style={{ minWidth: '140px' }}
                >
                  {recordingLoading ? 'Loading...' : 'Fetch Recording'}
                </button>
                
                {recordingUrl && (
                  <audio 
                    controls 
                    src={recordingUrl}
                    style={{ flex: 1, minWidth: '250px' }}
                  >
                    Your browser does not support the audio element.
                  </audio>
                )}
              </div>
              
              {recordingError && (
                <div className="error-message" style={{ marginTop: '10px' }}>
                  {recordingError}
                </div>
              )}
            </div>
          )}
          
          {/* Call Details Display */}
          {callDetails && (
            <div className="call-details-container">
              {/* Basic Call Information */}
              <div className="call-info-section">
                <h2>Call Information</h2>
                <div className="call-info-grid">
                  <div className="call-info-item">
                    <span className="info-label">Call ID:</span>
                    <span className="info-value">{callDetails.id}</span>
                  </div>
                  {callDetails.callSid && (
                    <div className="call-info-item">
                      <span className="info-label">Call SID:</span>
                      <span className="info-value">{callDetails.callSid}</span>
                    </div>
                  )}
                  {callDetails.practiceId && (
                    <div className="call-info-item">
                      <span className="info-label">Practice ID:</span>
                      <span className="info-value">{callDetails.practiceId}</span>
                    </div>
                  )}
                  {callDetails.customerPhone && (
                    <div className="call-info-item">
                      <span className="info-label">Customer Phone:</span>
                      <span className="info-value">{callDetails.customerPhone}</span>
                    </div>
                  )}
                  {callDetails.callType && (
                    <div className="call-info-item">
                      <span className="info-label">Call Type:</span>
                      <span className="info-value">{callDetails.callType}</span>
                    </div>
                  )}
                  {callDetails.startTime && (
                    <div className="call-info-item">
                      <span className="info-label">Start Time:</span>
                      <span className="info-value">{new Date(callDetails.startTime).toLocaleString()}</span>
                    </div>
                  )}
                  {callDetails.endTime && (
                    <div className="call-info-item">
                      <span className="info-label">End Time:</span>
                      <span className="info-value">{new Date(callDetails.endTime).toLocaleString()}</span>
                    </div>
                  )}
                  {callDetails.duration && (
                    <div className="call-info-item">
                      <span className="info-label">Duration:</span>
                      <span className="info-value">{callDetails.duration} seconds</span>
                    </div>
                  )}
                  {callDetails.phoneNumber && (
                    <div className="call-info-item">
                      <span className="info-label">Phone Number:</span>
                      <span className="info-value">{callDetails.phoneNumber}</span>
                    </div>
                  )}
                  {callDetails.status && (
                    <div className="call-info-item">
                      <span className="info-label">Status:</span>
                      <span className="info-value">{callDetails.status}</span>
                    </div>
                  )}
                  {(() => {
                    const isOutbound = ['recall', 'confirmation'].includes(callDetails.callType);
                    const agentNumber = isOutbound
                      ? callDetails.retellCall?.from_number
                      : callDetails.retellCall?.to_number;
                    return agentNumber ? (
                      <div className="call-info-item">
                        <span className="info-label">Retell Agent Number:</span>
                        <span className="info-value">{agentNumber}</span>
                      </div>
                    ) : null;
                  })()}
                  {callDetails.practiceTimezone && (
                    <div className="call-info-item">
                      <span className="info-label">Practice Timezone:</span>
                      <span className="info-value">{callDetails.practiceTimezone}</span>
                    </div>
                  )}
                  {callDetails.callTs && (
                    <div className="call-info-item">
                      <span className="info-label">Call Timestamp (raw):</span>
                      <span className="info-value" style={{ fontFamily: 'monospace' }}>{callDetails.callTs}</span>
                    </div>
                  )}
                </div>
                {callDetails.callTs && (
                  <div className="call-info-item" style={{ marginTop: '10px' }}>
                    <span className="info-label">Call Time:</span>
                    <span className="info-value" style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>
                        {(() => {
                          const now = Date.now();
                          const callTime = callDetails.callTs * 1000;
                          const diffMs = now - callTime;
                          const diffMins = Math.floor(diffMs / 60000);
                          const diffHours = Math.floor(diffMins / 60);
                          const diffDays = Math.floor(diffHours / 24);
                          
                          if (diffMins < 1) return 'just now';
                          if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
                          if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
                          return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
                        })()}
                      </span>
                      <span style={{ color: '#666', fontSize: '0.85em' }}>
                        ({new Date(callDetails.callTs * 1000).toLocaleString()})
                      </span>
                    </span>
                  </div>
                )}
              </div>
              
              {/* Retell Call Details */}
              {callDetails.retellCall && (
                <div className="retell-call-section">
                  <h2>Retell Call Details</h2>
                  <div className="retell-info-grid">
                    {callDetails.retellCall.call_id && (
                      <div className="call-info-item">
                        <span className="info-label">Retell Call ID:</span>
                        <span className="info-value">{callDetails.retellCall.call_id}</span>
                      </div>
                    )}
                    {callDetails.retellCall.agent_id && (
                      <div className="call-info-item">
                        <span className="info-label">Agent ID:</span>
                        <span className="info-value">{callDetails.retellCall.agent_id}</span>
                      </div>
                    )}
                    {callDetails.retellCall.call_type && (
                      <div className="call-info-item">
                        <span className="info-label">Call Type:</span>
                        <span className="info-value">{callDetails.retellCall.call_type}</span>
                      </div>
                    )}
                    {callDetails.retellCall.start_timestamp && (
                      <div className="call-info-item">
                        <span className="info-label">Start Timestamp:</span>
                        <span className="info-value">{new Date(callDetails.retellCall.start_timestamp).toLocaleString()}</span>
                      </div>
                    )}
                    {callDetails.retellCall.end_timestamp && (
                      <div className="call-info-item">
                        <span className="info-label">End Timestamp:</span>
                        <span className="info-value">{new Date(callDetails.retellCall.end_timestamp).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Raw Data Section (Collapsible) */}
              <div className="raw-data-section">
                <details>
                  <summary>
                    <h3>Raw Call Data</h3>
                  </summary>
                  <div className="raw-data-container">
                    <pre>{JSON.stringify(callDetails, null, 2)}</pre>
                  </div>
                </details>
              </div>
            </div>
          )}
          
          {/* No Data Message */}
          {!callDetailsLoading && !callDetails && !callDetailsError && searchCallId && (
            <div className="no-data">
              <p>No call details found. Enter a call ID and click "Search Call" to retrieve call information.</p>
            </div>
          )}
          
          {/* Instructions */}
          {!searchCallId && (
            <div className="instructions">
              <h3>Instructions:</h3>
              <ol>
                <li>Enter a call ID in the search field above</li>
                <li>Click "Search Call" or press Enter to retrieve call details</li>
                <li>View call information, including transcript from retellCall object</li>
                <li>Use "Clear" to reset the search and results</li>
              </ol>
            </div>
          )}
        </div>
      )}
      {/* ---------- ONBOARDING TAB ---------- */}
      {activeTab === 'onboardingView' && (
        <div className="tab-content">
          <OnboardingViewWrapper />
        </div>
      )}
      
      {/* ---------- DEMO CALLS TAB ---------- */}
      {activeTab === 'demoCalls' && <DemoCalls />}

      {/* ---------- SALES TOUCHPOINTS TAB ---------- */}
      {activeTab === 'salesTouchpoints' && <SalesTouchpoints />}

      {/* ARR Dashboard moved to NewDashboard */}
    </div>
  );
}

export default App;
